import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Cross, Loader2, AlertCircle, CheckCircle2, Clock } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RegisterPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  
  const [validating, setValidating] = useState(true);
  const [inviteValid, setInviteValid] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteInfo, setInviteInfo] = useState(null);
  
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [pin, setPin] = useState(['', '', '', '']);
  const [confirmPin, setConfirmPin] = useState(['', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const pinRefs = [
    React.useRef(null),
    React.useRef(null),
    React.useRef(null),
    React.useRef(null),
  ];

  const confirmPinRefs = [
    React.useRef(null),
    React.useRef(null),
    React.useRef(null),
    React.useRef(null),
  ];

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await axios.get(`${API}/invites/validate/${token}`);
      setInviteValid(true);
      setInviteInfo(response.data);
    } catch (err) {
      setInviteError(err.response?.data?.detail || 'Invalid invite link');
    } finally {
      setValidating(false);
    }
  };

  const handlePinChange = (index, value, isConfirm = false) => {
    if (!/^\d*$/.test(value)) return;
    
    const newPin = isConfirm ? [...confirmPin] : [...pin];
    newPin[index] = value.slice(-1);
    
    if (isConfirm) {
      setConfirmPin(newPin);
      if (value && index < 3) {
        confirmPinRefs[index + 1].current?.focus();
      }
    } else {
      setPin(newPin);
      if (value && index < 3) {
        pinRefs[index + 1].current?.focus();
      }
    }
  };

  const handlePinKeyDown = (index, e, isConfirm = false) => {
    const refs = isConfirm ? confirmPinRefs : pinRefs;
    const currentPin = isConfirm ? confirmPin : pin;
    
    if (e.key === 'Backspace' && !currentPin[index] && index > 0) {
      refs[index - 1].current?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const pinValue = pin.join('');
    const confirmPinValue = confirmPin.join('');

    if (!name.trim()) {
      setError('Please enter your name');
      return;
    }

    if (!phone.trim()) {
      setError('Please enter your phone number');
      return;
    }

    if (pinValue.length !== 4) {
      setError('Please enter a 4-digit PIN');
      return;
    }

    if (pinValue !== confirmPinValue) {
      setError('PINs do not match');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, {
        token,
        name: name.trim(),
        phone: phone.trim(),
        pin: pinValue
      });

      // Auto-login after registration
      localStorage.setItem('kp_token', response.data.token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
      
      // Redirect to gallery
      window.location.href = '/gallery';
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  if (!inviteValid) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
          <AlertCircle className="w-8 h-8 text-red-600" />
        </div>
        <h1 className="font-serif text-2xl text-slate-900 mb-2">Invalid Link</h1>
        <p className="text-slate-500 text-center mb-6">{inviteError}</p>
        <Button
          onClick={() => navigate('/')}
          variant="outline"
          className="rounded-full"
        >
          Go to Login
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Hero Section */}
      <div 
        className="h-[30vh] relative bg-cover bg-center"
        style={{ 
          backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(248,250,252,1)), url('https://images.unsplash.com/photo-1636051551573-15d6cb59db52?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200')` 
        }}
      >
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
          <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-lg mb-3">
            <Cross className="w-7 h-7 text-violet-600" strokeWidth={1.5} />
          </div>
          <h1 className="font-serif text-3xl text-slate-900 tracking-tight">Join Kings Photos</h1>
          <p className="text-slate-600 mt-1">Create your account</p>
        </div>
      </div>

      {/* Registration Form */}
      <div className="flex-1 -mt-6 relative z-10">
        <div className="bg-white rounded-t-[32px] shadow-xl px-6 py-8 min-h-full">
          {/* Invite Info */}
          <div className="bg-green-50 rounded-2xl p-4 mb-6 flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
            <div>
              <p className="text-green-800 font-medium">You've been invited!</p>
              <p className="text-green-700 text-sm">
                Invited by {inviteInfo?.created_by_name}
              </p>
              <p className="text-green-600 text-xs mt-1 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Link expires {formatDistanceToNow(new Date(inviteInfo?.expires_at), { addSuffix: true })}
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="max-w-sm mx-auto space-y-5">
            {/* Name Input */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Your Name
              </label>
              <Input
                data-testid="register-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your full name"
                className="h-14 text-lg rounded-2xl border-slate-200 bg-slate-50 focus:bg-white focus:ring-violet-200 focus:border-violet-400"
              />
            </div>

            {/* Phone Input */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Phone Number
              </label>
              <Input
                data-testid="register-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Enter your phone number"
                className="h-14 text-lg rounded-2xl border-slate-200 bg-slate-50 focus:bg-white focus:ring-violet-200 focus:border-violet-400"
              />
            </div>

            {/* PIN Input */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Create 4-Digit PIN
              </label>
              <div className="pin-input-container">
                {pin.map((digit, index) => (
                  <input
                    key={index}
                    ref={pinRefs[index]}
                    data-testid={`register-pin-${index}`}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handlePinChange(index, e.target.value)}
                    onKeyDown={(e) => handlePinKeyDown(index, e)}
                    className="pin-input-slot"
                  />
                ))}
              </div>
            </div>

            {/* Confirm PIN Input */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Confirm PIN
              </label>
              <div className="pin-input-container">
                {confirmPin.map((digit, index) => (
                  <input
                    key={index}
                    ref={confirmPinRefs[index]}
                    data-testid={`register-confirm-pin-${index}`}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handlePinChange(index, e.target.value, true)}
                    onKeyDown={(e) => handlePinKeyDown(index, e, true)}
                    className="pin-input-slot"
                  />
                ))}
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div data-testid="register-error" className="text-red-500 text-sm text-center bg-red-50 p-3 rounded-xl flex items-center justify-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            {/* Submit Button */}
            <Button
              data-testid="register-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full h-14 text-lg font-medium rounded-full bg-violet-600 hover:bg-violet-700 text-white shadow-lg shadow-violet-200 transition-all hover:-translate-y-0.5"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Creating account...
                </>
              ) : (
                'Create Account'
              )}
            </Button>

            <p className="text-center text-sm text-slate-500">
              Already have an account?{' '}
              <button 
                type="button"
                onClick={() => navigate('/')}
                className="text-violet-600 hover:underline"
              >
                Sign in
              </button>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
